import { extensionsWithoutRefractor } from "@repo/fika-blog/src/extensionsWithoutRefractor"
import { renderToText } from "@repo/fika-blog/src/renderer/pm/text/text"
import type { Post } from "@repo/fika-shared/src/loader/post-schema.js"
import type { Publisher } from "@repo/fika-shared/src/loader/publisher-schema.js"
import type { Story } from "@repo/fika-shared/src/loader/story-schema.js"
import type { ExperimentalNoIndexDiff } from "@rocicorp/replicache"
import CodeBlock from "@tiptap/extension-code-block"
import type { DocumentData, MergedDocumentSearchResults } from "flexsearch"
import { indexBy } from "remeda"
import { type Accessor, createMemo, createResource } from "solid-js"
import { z } from "zod"
import {
  postsIndex,
  publisherIndex,
  type SearchDocument,
  SearchDocumentSchema,
  type SearchIndex,
  storyIndex,
} from "./search-index"

const MIN_SEARCH_LENGTH = 2

type Batch<T> = {
  insert: T[]
  update: T[]
  dels: string[]
}
type SearchResults = MergedDocumentSearchResults<DocumentData> | undefined

type DocumentTransformer<T extends { id: string }> = (
  items: T[],
) => Promise<SearchDocument[]>

const indexes = {
  publisher: publisherIndex,
  post: postsIndex,
  story: storyIndex,
} as const

type Namespace = keyof typeof indexes

const publisherTransformer: DocumentTransformer<Publisher> = async (
  publishers: Publisher[],
) => {
  return publishers.map(publisher => ({
    id: publisher.id,
    title: publisher.dc_title,
    description: publisher.dc_description,
    body: null,
  }))
}

const postTransformer: DocumentTransformer<Post> = async (posts: Post[]) => {
  return posts.flatMap(post => {
    if (!post.latest) return []

    let body: string | null = null
    if (post.json) {
      try {
        body = renderToText({
          extensions: [
            ...extensionsWithoutRefractor({ dropcursor: false }),
            CodeBlock,
          ],
          doc: JSON.parse(post.json),
        })
      } catch (error) {
        console.error("Error rendering post content:", error)
      }
    }

    return {
      id: post.id,
      title: post.title,
      description: post.description,
      body,
    }
  })
}

const storyTransformer: DocumentTransformer<Story> = async (
  stories: Story[],
) => {
  const ids = stories.map(s => s.id)

  try {
    const response = await fetch(
      `${import.meta.env.VITE_FIKA_BACKEND_URL}/api/stories/content`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      },
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch story content: ${response.statusText}`)
    }

    const json = await response.json()
    return z.array(SearchDocumentSchema).parse(json)
  } catch (error) {
    console.error("Error fetching story content:", error)
    return []
  }
}

async function processBatch<T extends { id: string }>(
  batch: Batch<T>,
  index: SearchIndex,
  transformer: DocumentTransformer<T>,
) {
  async function getDocuments(requestedIds: string[]) {
    const itemsById = indexBy(batch.insert, item => item.id)
    const requestedItems = requestedIds
      .map(id => itemsById[id])
      .filter((item): item is T => item !== undefined)

    return await transformer(requestedItems)
  }

  try {
    if (batch.insert.length > 0) {
      const ids = batch.insert.map(item => item.id)
      await index.addDocuments(ids, getDocuments)
    }

    if (batch.update.length > 0) {
      const ids = batch.update.map(item => item.id)
      await index.updateDocuments(ids, getDocuments)
    }

    if (batch.dels.length > 0) {
      await index.deleteDocuments(batch.dels)
    }
  } catch (error) {
    console.error("Error processing batch:", error)
  }
}

function categorizeDiffs(diffs: ExperimentalNoIndexDiff) {
  const publisherBatch: Batch<Publisher> = {
    insert: [],
    update: [],
    dels: [],
  }
  const postBatch: Batch<Post> = { insert: [], update: [], dels: [] }
  const storyBatch: Batch<Story> = { insert: [], update: [], dels: [] }

  const batches = {
    publisher: publisherBatch,
    post: postBatch,
    story: storyBatch,
  }

  for (const diff of diffs) {
    const [namespace, id] = diff.key.split("/")

    if (!namespace || !id) {
      console.warn("Invalid diff key format:", diff.key)
      continue
    }

    const batch = batches[namespace as Namespace]
    if (!batch) continue

    if (diff.op === "del") {
      batch.dels.push(id)
    } else if (diff.op === "add" || diff.op === "change") {
      const model = diff.newValue

      if (!model || !(model as any)?.backend) {
        continue
      }

      if (diff.op === "add") {
        batch.insert.push(model as any)
      } else if (diff.op === "change") {
        batch.update.push(model as any)
      }
    }
  }

  return batches
}

export async function sync(diffs: ExperimentalNoIndexDiff) {
  try {
    const batches = categorizeDiffs(diffs)

    await Promise.all([
      processBatch(batches.publisher, indexes.publisher, publisherTransformer),
      processBatch(batches.post, indexes.post, postTransformer),
      processBatch(batches.story, indexes.story, storyTransformer),
    ])
  } catch (error) {
    console.error("Error during search sync:", error)
  }
}

export function createSearch(getTerm: Accessor<string>, namespace: Namespace) {
  const index = indexes[namespace]

  if (!index) {
    throw new Error(`Index ${namespace} not found`)
  }

  const [searchResults] = createResource(getTerm, async term => {
    if (term.length < MIN_SEARCH_LENGTH) {
      return undefined
    }

    try {
      return await index.search(decodeURIComponent(term))
    } catch (error) {
      console.error(`Search error for namespace ${namespace}:`, error)
      return []
    }
  })

  return createMemo<SearchResults>(prev => {
    if (searchResults.state === "ready") {
      return searchResults()
    }
    return prev
  }, searchResults())
}
